import type { CommunityDetail } from '@pba/react-network';
import cn from 'classnames';
import type { LabelInValue } from 'models';
import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  type ButtonProperties,
  Col,
  Form,
  FormActions,
  Icon,
  Item,
  Modal,
  type ModalOptions,
  Row,
  Text,
} from 'ui';
import { CommunityInfo } from './community-info';
import { EmailInviteModal } from './email-invite-modal';
import { useCopyInvitationLink } from './hooks';
import { MembersInviteModal } from './members-invite-modal';
import { ShareQrCodeModal } from './share-qr-code-modal';
import { SmsInviteModal } from './sms-invite-modal';

export type InviteFriendsModalOptions = {
  community: CommunityDetail;
};

type InviteFriendsFormValues = {
  community: LabelInValue;
};

type ModalState =
  | {
      community: LabelInValue;
      type: 'email' | 'members' | 'qr' | 'sms';
    }
  | {
      community: undefined;
      type: undefined;
    };

type P = {
  onCancel: () => void;
  options: ModalOptions<InviteFriendsModalOptions>;
};

type ValidateAndExecuteCallback = (values: InviteFriendsFormValues) => Promise<void> | void;

const InviteFriendsModal = ({ onCancel, options }: P) => {
  const [form] = Form.useForm<InviteFriendsFormValues>();
  const [modalsOpen, setModalsOpen] = useState<ModalState>({ community: undefined, type: undefined });

  const { copyInvitationLink, isCopyingInvitationLink } = useCopyInvitationLink();

  const validateAndExecute = useCallback(
    async (callback: ValidateAndExecuteCallback) => {
      try {
        const values = await form.validateFields();
        await callback(values);
      } catch {}
    },
    [form],
  );

  const buttons: Array<{ key: string } & ButtonProperties> = useMemo(
    () => [
      {
        children: 'Copy link',
        icon: <Icon icon="link-line" />,
        key: 'link',
        loading: isCopyingInvitationLink,
        onClick: async () =>
          validateAndExecute(async (values: InviteFriendsFormValues) =>
            copyInvitationLink({ communityId: values.community.value }),
          ),
      },
      {
        children: 'QR code',
        icon: <Icon icon="qrcode-line" />,
        key: 'qr-code',
        onClick() {
          void validateAndExecute((values: InviteFriendsFormValues) => {
            setModalsOpen({ community: values.community, type: 'qr' });
          });
        },
      },
      {
        children: 'Member select',
        icon: <Icon icon="group-line" />,
        key: 'members',
        onClick() {
          void validateAndExecute((values: InviteFriendsFormValues) => {
            setModalsOpen({ community: values.community, type: 'members' });
          });
        },
      },
      {
        children: 'E-mail',
        icon: <Icon icon="mail-line" />,
        key: 'email',
        onClick() {
          void validateAndExecute((values: InviteFriendsFormValues) => {
            setModalsOpen({ community: values.community, type: 'email' });
          });
        },
      },
      {
        children: 'SMS',
        icon: <Icon icon="message-2-line" />,
        key: 'sms',
        onClick() {
          void validateAndExecute((values: InviteFriendsFormValues) => {
            setModalsOpen({ community: values.community, type: 'sms' });
          });
        },
      },
    ],
    [copyInvitationLink, isCopyingInvitationLink, validateAndExecute],
  );

  return (
    <>
      <Modal
        className={cn({
          'visually-hidden': modalsOpen.type !== undefined,
        })}
        isOpened={options.isOpened}
        onCancel={onCancel}
        title="Invite friends"
      >
        {options.isOpened ? (
          <Modal.HugContent>
            <Form
              form={form}
              initialValues={{
                community: {
                  label: options.community.name,
                  value: options.community.id,
                },
              }}
            >
              <Item hideLabel label="Select community" name="community" rules={[{ required: true }]}>
                <CommunityInfo community={options.community} />
              </Item>
            </Form>
            <Row gutter={[0, 'xs']}>
              <Col span={24}>
                <Text size="small">How would you like to invite someone?</Text>
              </Col>
              {buttons.map(({ key, ...buttonProperties }) => (
                <Col key={key} span={24}>
                  <Button block type="secondary" {...buttonProperties} />
                </Col>
              ))}
            </Row>
            <Modal.HugContent.Footer>
              <FormActions right={{ children: 'Cancel', onClick: onCancel }} />
            </Modal.HugContent.Footer>
          </Modal.HugContent>
        ) : null}
      </Modal>
      <ShareQrCodeModal
        onCancel={() => {
          setModalsOpen({ community: undefined, type: undefined });
        }}
        options={modalsOpen.type === 'qr' ? { community: modalsOpen.community, isOpened: true } : { isOpened: false }}
      />
      <MembersInviteModal
        onCancel={() => {
          setModalsOpen({ community: undefined, type: undefined });
        }}
        options={
          modalsOpen.type === 'members' ? { community: modalsOpen.community, isOpened: true } : { isOpened: false }
        }
      />
      <EmailInviteModal
        onCancel={() => {
          setModalsOpen({ community: undefined, type: undefined });
        }}
        options={
          modalsOpen.type === 'email' ? { community: modalsOpen.community, isOpened: true } : { isOpened: false }
        }
      />
      <SmsInviteModal
        onCancel={() => {
          setModalsOpen({ community: undefined, type: undefined });
        }}
        options={modalsOpen.type === 'sms' ? { community: modalsOpen.community, isOpened: true } : { isOpened: false }}
      />
    </>
  );
};

export default InviteFriendsModal;
